import { useLingui } from '@lingui/react/macro';

import { getRequestorTypeLabel } from '@/models/request';
import { isExternalUserForOrganization } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

type GetRequestNameParams = {
  request: {
    organization: { id: string; name: string };
    client: { name: string | null } | null;
    store: { name: string } | null;
  };
  isClientEnabled: boolean;
};

const useGetRequestName = () => {
  const { currentSession } = useCurrentSession();
  const { t, i18n } = useLingui();

  return ({ request, isClientEnabled }: GetRequestNameParams) => {
    const isExternalUser = isExternalUserForOrganization(currentSession, request.organization.id);

    if (request.client && request.store) {
      if (isExternalUser) {
        return {
          major: request.store.name,
          minor: i18n._(getRequestorTypeLabel('client-via-store')),
        };
      } else {
        return {
          major:
            isClientEnabled && !!request.client.name
              ? request.client.name
              : i18n._(getRequestorTypeLabel('client-via-store')),
          minor: request.store.name,
        };
      }
    }

    if (request.client && !request.store) {
      if (isExternalUser) {
        return {
          major: request.organization.name,
          minor: i18n._(getRequestorTypeLabel('client')),
        };
      } else {
        return {
          major:
            isClientEnabled && !!request.client.name
              ? request.client.name
              : i18n._(getRequestorTypeLabel('client')),
          minor: null,
        };
      }
    }

    if (request.store && !request.client) {
      if (isExternalUser) {
        return {
          major: request.store.name,
          minor: i18n._(getRequestorTypeLabel('store')),
        };
      } else {
        return {
          major: request.store.name,
          minor: null,
        };
      }
    }

    return {
      major: t({ id: 'request.requestor.unknown', message: 'Unknown requester' }),
      minor: null,
    };
  };
};

export const useRequestName = (params: GetRequestNameParams) => {
  const getRequestName = useGetRequestName();

  return getRequestName(params);
};
